import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { Attribute } from 'src/app/shared/models/attributes/attribute';
import { AttributeDefinition } from 'src/app/shared/models/attributes/attributeDefinition';
import { AttributeWrite } from 'src/app/shared/models/attributes/attributeWrite';
import { FlatAttribute } from 'src/app/shared/models/attributes/flatAttribute';
import { Observable, Subscription } from 'rxjs';
import { BaseService } from 'src/app/shared/services/base.service';

import * as _ from 'lodash';
import { AttributeUiType } from 'src/app/shared/models/attributes/attributeUiType';
import { AttributeDefinitionValueListItem } from 'src/app/shared/models/attributes/attributeDefinitionValueListItem';
import { AttributesEditorDataSource } from 'src/app/shared/components/attributes-editor/attributesEditorDataSource';

import { DatePipe } from '@angular/common';
import { AttributeEditorMode } from 'src/app/shared/models/attributes/attributeEditorMode';
import { ComplexAttributeWrite } from 'src/app/shared/models/attributes/complexAttributeWrite';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/auth/auth.service';
import { AttributeService } from 'src/app/shared/services/attribute.service';
import { CustomFieldsDescription } from '../../custom-fields-description/custom-fields-description.component';

@Component({
  selector: 'custom-field-inline-editor',
  templateUrl: './custom-field-inline-editor.component.html',
  styleUrls: ['./custom-field-inline-editor.component.scss'],
})
export class CustomFieldInlineEditorComponent implements OnDestroy {
  private subscriptions: Array<Subscription> = [];
  private _customFieldValues: Attribute[];
  private _customField: AttributeDefinition;
  private firstOpen: boolean = true;
  showPassword: boolean = false;
  visibility_icon: string = 'visibility';
  encryptedFieldValue: string = '***';

  readonly noValueText: string = 'Click to add';

  public isEditing: boolean = false;
  public isSaving: boolean = false;
  public flatAttribute: FlatAttribute;
  public editorDataSource = new AttributesEditorDataSource();
  teamId;
  personId;

  @Input() entityId: string = '';
  @Input() attributeEditorMode: AttributeEditorMode = AttributeEditorMode.PersonDetails;

  @Input()
  get customField(): AttributeDefinition {
    return this._customField;
  }
  set customField(value: AttributeDefinition) {
    this._customField = value;
    this.buildFlatAttributeRepresentation();
  }
  @Input()
  get customFieldValues(): Attribute[] {
    return this._customFieldValues;
  }
  set customFieldValues(value: Attribute[]) {
    this._customFieldValues = value;
    this.buildFlatAttributeRepresentation();
    if (this._customField?.uiType == AttributeUiType.Complex) {
      let cloned = _.cloneDeep(this.customFieldValues);
      this.editorDataSource.load(cloned, [this.customField], this.filterAttribute());
    }
  }
  @Input()
  editable: boolean = true;
  @Input()
  useDescription: boolean = false;
  @Input()
  saveFn: (attributeDefinitionId: string, attributeWrite: AttributeWrite) => Observable<any>;
  @Input()
  deleteFn: (attributeDefinitionId: string) => Observable<any>;
  @Output() addOrUpdateComplexValue: EventEmitter<{
    attributeDefinitionId: string;
    writeModel: ComplexAttributeWrite[];
  }> = new EventEmitter<{ attributeDefinitionId: string; writeModel: ComplexAttributeWrite[] }>();

  constructor(
    private snackBar: BaseService,
    private authService: AuthService,
    private changeDetectorRef: ChangeDetectorRef,
    private datePipe: DatePipe,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private attributeService: AttributeService
  ) {
    this.route.params.subscribe((params) => {
      this.personId = params['personId'];
    });
    this.route.parent.params.subscribe((params) => {
      this.teamId = params['id'];
    });
  }

  get firstElementValueListItem(): AttributeDefinitionValueListItem {
    return this._customFieldValues[0]?.attributeDefinitionValueListItem;
  }

  get personDetailsMode(): AttributeEditorMode {
    return AttributeEditorMode.PersonDetails;
  }

  private buildFlatAttributeRepresentation() {
    this.flatAttribute = this.customFieldValues?.length
      ? FlatAttribute.fromAttributes(this.customFieldValues)[0]
      : this.customField
      ? FlatAttribute.fromAttributeDefinition(this.customField)
      : null;
    if (!this.flatAttribute?.attributes) {
      this.encryptedFieldValue = '';
    }
  }

  getInfoTooltipText(): string {
    return this.isStale()
      ? 'This field has not been updated in over a year. It was last updated on ' +
          this.datePipe.transform(this.flatAttribute?.updatedOn, 'mediumDate') +
          ' by ' +
          this.flatAttribute.updatedBy
      : 'Last updated on ' +
          this.datePipe.transform(this.flatAttribute?.updatedOn, 'mediumDate') +
          ' by ' +
          this.flatAttribute.updatedBy;
  }

  showHistoricalText() {
    let fielddataHistoricalModalComponent = this.dialog.open(CustomFieldsDescription, {
      data: {
        attributeName: this.customField.name,
        id: this.personId ? this.personId : this.teamId,
        description: this.customField.adminDocumentation,
        type: this.customField.uiType,
      },
    });
    fielddataHistoricalModalComponent.afterClosed().subscribe((result) => {
      fielddataHistoricalModalComponent = null;
    });
  }

  isStaff() {
    return this.authService.staffLevelAccess;
  }

  public isStale(): boolean {
    return (
      new Date(this.flatAttribute?.updatedOn) <
      new Date(new Date().setFullYear(new Date().getFullYear() - 1))
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  openEditor() {
    if (!this.editable) return;

    this.isEditing = true;
    this.changeDetectorRef.detectChanges();

    if (this.firstOpen) {
      this.firstOpen = false;
      let cloned = _.cloneDeep(this.customFieldValues);
      this.editorDataSource.load(cloned, [this.customField], this.filterAttribute());
    }
  }

  formatDisplayValue(attribute: Attribute): string {
    let displayValue = '';

    if (attribute && attribute.type === 'Date' && attribute.hasValue()) {
      displayValue = this.datePipe.transform(attribute.value, 'mediumDate');
    } else if (attribute && attribute.type === 'DateRange') {
      displayValue = attribute.hasValue()
        ? attribute.value.replace('|', '-')
        : this.editable
        ? this.noValueText
        : '';
    } else {
      displayValue = attribute.hasValue() ? attribute.value : this.editable ? this.noValueText : '';
    }

    return displayValue;
  }

  doSave(attributeChanged = true, isDocumentSaving = false) {
    if (attributeChanged == false) {
      this.cancel();
      return;
    }
    let comp = this;
    if (comp.formIsValid() && !comp.isSaving) {
      comp.disableSubmitButton();

      let attr = AttributeWrite.fromAttributes(comp.editorDataSource.editable);
      let sub = comp.handleAttributeBasedOnValue(attr, isDocumentSaving);
      comp.subscriptions.push(sub);
      sub.add(() => comp.enableSubmitButton());
    }
  }

  handleAttributeBasedOnValue(attribute: AttributeWrite, isDocumentSaving = false): Subscription {
    let comp = this;
    if (attribute && attribute.hasValue()) {
      return comp.saveFn(comp.customField.id, attribute).subscribe(() => {
        if (!isDocumentSaving) {
          comp.snackBar.openSnackBar(`${comp.customField.name} has been updated.`, true);
          if (comp.customField.uiType == AttributeUiType.EncryptedField) {
            this.visibility_icon = 'visibility';
            this.encryptedFieldValue = '***';
            this.showPassword = false;
          }
          comp.cancel();
        }
      });
    } else {
      return comp.deleteFn(comp.customField.id).subscribe(() => {
        if (!isDocumentSaving) {
          comp.snackBar.openSnackBar(`${comp.customField.name} has been deleted.`, true);
          comp.cancel();
        }
      });
    }
  }

  preventDefault(event: { preventDefault: () => void }) {
    // prevent the cancel button's mouse-down event from triggering the attribute editor's
    // blur event, which saves the attribute value
    event.preventDefault();
  }

  cancel() {
    this.isEditing = false;
  }

  formIsValid(): boolean {
    return this.editorDataSource.isValid;
  }

  togglePasswordVisibility(value: any): void {
    this.showPassword = !this.showPassword;
    if (this.showPassword) {
      this.visibility_icon = 'visibility_off';
      this.attributeService.decryptAttribute(this.customField.id, value).subscribe((result) => {
        if (result) {
          this.encryptedFieldValue = result;
        }
      });
    } else {
      this.visibility_icon = 'visibility';
      this.encryptedFieldValue = '***';
    }
  }

  private disableSubmitButton() {
    this.isSaving = true;
  }

  private enableSubmitButton() {
    this.isSaving = false;
  }

  private filterAttribute() {
    let comp = this;
    return (attributes: Attribute[]) =>
      attributes.filter((a) => a.attributeDefinitionId == comp.customField.id);
  }
}
